export type DesiredTrend = 'up' | 'down'
export type ComparisonTone = 'favorable' | 'unfavorable' | 'neutral'
export type ComparisonDirection = 'up' | 'down' | 'neutral'

export interface ComparisonPresentation {
  direction: ComparisonDirection
  tone: ComparisonTone
  label: string
}

export const NOT_COMPUTABLE_LABEL = 'NC'

export const SAME_WEEKDAYS_MAX_WINDOW_DAYS = 6

/**
 * Décalage de la fenêtre comparée : sa propre durée, sauf ≤ 6 jours où l'on compare aux mêmes jours
 * de la semaine précédente. Un décalage de 7 signifie donc toujours « semaine précédente » (S-1).
 * Aligné sur le backend `computeDefaultComparisonPeriod`.
 */
export function comparisonShiftDays(windowDays: number): number {
  return windowDays <= SAME_WEEKDAYS_MAX_WINDOW_DAYS ? 7 : windowDays
}

export const COMPARISON_TEXT_CLASS_BY_TONE: Record<ComparisonTone, string> = {
  favorable: 'text-[#4F6A47]',
  unfavorable: 'text-[#EF8031]',
  neutral: 'text-[#6B6B6B]',
}

export function getComparisonPresentation({
  value,
  desiredTrend,
  unit = '%',
  maximumFractionDigits = 1,
}: {
  value: number | null
  desiredTrend: DesiredTrend
  unit?: string
  maximumFractionDigits?: 0 | 1 | 'auto'
}): ComparisonPresentation {
  if (value === null) {
    return {
      direction: 'neutral',
      tone: 'neutral',
      label: NOT_COMPUTABLE_LABEL,
    }
  }

  const direction: ComparisonDirection = value >= 0 ? 'up' : 'down'
  const isFavorable = value === 0 || direction === desiredTrend
  const autoDigits = Math.abs(value) < 1 ? 1 : 0
  const resolvedDigits = maximumFractionDigits === 'auto' ? autoDigits : maximumFractionDigits
  const formattedValue = value.toLocaleString('fr-FR', {
    maximumFractionDigits: resolvedDigits,
  })

  return {
    direction,
    tone: isFavorable ? 'favorable' : 'unfavorable',
    label: `${value >= 0 ? '+' : ''}${formattedValue}${unit}`,
  }
}
